import { parseAbsoluteToLocal } from "@internationalized/date"
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { GatedSessionLookupItem } from "ipctg-inflow-types/InFlowDB"
import { PromoFailReason } from "ipctg-inflow-types/InFlowEnums"
import { GatedPromoRegisterPOSTRequest } from "ipctg-inflow-types/InFlowGatedAPI"
import { useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button, LinkButton } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Separator from "@/globalComponents/atoms/Separator"
import Step from "@/globalComponents/atoms/Step"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import { formatZonedDateTimeWithTimeZone } from "@/utilities/etc"
import { GatedPromoRegisterError } from "@/utilities/expectedErrors"
import { requiredRule } from "@/utilities/form"
import {
  gatedSessionDetailsQueryOptions,
  gatedSetupIntentQueryOptions,
} from "@/utilities/hooks/queries/gated"
import { useTheme } from "@/utilities/hooks/useTheme"
import useToggle from "@/utilities/hooks/useToggle"
import { normalizedAPILicensePlate } from "@/utilities/normalizers"

import PayingCashLauncher from "../../-components/PayingCash/PayingCashLauncher"

export const Route = createFileRoute(
  "/qr/$qrLookupID/gated/register/$sessionID/cart"
)({
  staticData: {
    pageName: "Cart",
  },
  component: RouteComponent,
})

export interface DiscountCodeSchema {
  promoCode: string
}

function RouteComponent() {
  const { sessionID } = Route.useParams()
  const { reregister, express } = Route.useSearch()
  const queryClient = useQueryClient()
  const navigate = Route.useNavigate()
  const { t } = useTranslation(["gated", "errors", "common"])
  const { data: sessionRsp } = useSuspenseQuery(
    gatedSessionDetailsQueryOptions({ sessionID })
  )
  const { metadata, drawer } = useTheme()
  const cashPaymentMode = metadata?.gated?.payment?.cash?.mode

  const [showDiscountCodeField, toggleShowDiscountCodeField] = useToggle(false)
  const [discountCodeApplied, setDiscountCodeApplied] = useState<{
    code: string
    redeemCopy: string | null
  } | null>(null)
  const form = useForm<DiscountCodeSchema>({
    defaultValues: {
      promoCode: "",
    },
  })

  const { mutate, isPending } = useMutation({
    mutationFn,
    throwOnError: true,
  })

  return (
    <>
      <Heading className="center" level={3} as={1}>
        {t("register.cart.header", { ns: "gated" })}
      </Heading>
      <Step step={3} of={4} />
      <Card>
        <Heading level={1} as={2} className="center">
          {t("register.cart.subhead", { ns: "gated" })}
        </Heading>
        <p className="body--2 center">
          {!discountCodeApplied && (
            <Button
              buttonType="primary"
              presentation="link"
              onPress={() => {
                if (showDiscountCodeField) {
                  setDiscountCodeApplied(null)
                }
                toggleShowDiscountCodeField()
              }}
            >
              {showDiscountCodeField
                ? t("register.cart.promoCode.remove", { ns: "gated" })
                : t("register.cart.promoCode.add", { ns: "gated" })}
            </Button>
          )}
        </p>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          {showDiscountCodeField && !discountCodeApplied && (
            <fieldset disabled={isPending}>
              <Controller<DiscountCodeSchema>
                name="promoCode"
                control={form.control}
                rules={{
                  required: requiredRule(
                    t("common.required", { ns: "errors" })
                  ),
                }}
                render={({
                  field: { ref, onChange, ...rest },
                  fieldState: { error, invalid },
                }) => (
                  <TextField
                    {...rest}
                    onChange={(v) => onChange(v.trim().toUpperCase())}
                    isInvalid={invalid}
                    aria-label={t("register.cart.promoCode.fieldLabel", {
                      ns: "gated",
                    })}
                  >
                    <div className="promo-code--field field--input">
                      <TextField.Input innerRef={ref} />
                      <Button
                        type="submit"
                        buttonType="default"
                        isPending={isPending}
                      >
                        {t("register.cart.promoCode.apply", { ns: "gated" })}
                      </Button>
                    </div>
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
            </fieldset>
          )}
          {discountCodeApplied && (
            <Text level={2} className="highlighted-cell center">
              {discountCodeApplied.redeemCopy || (
                <>
                  <b>{discountCodeApplied.code}</b>{" "}
                  {t("register.cart.promoCode.successfullyApplied", {
                    ns: "gated",
                  })}
                </>
              )}
            </Text>
          )}
        </form>
        <LinkButton
          from={Route.fullPath}
          to="/qr/$qrLookupID/gated/register/$sessionID/checkout"
          search={{ reregister, express }}
          buttonType="primary"
        >
          <Icon name="creditCard" slot="left" />
          {t("register.cart.submit", { ns: "gated" })}
        </LinkButton>
        <PaymentMethod />
      </Card>
      {cashPaymentMode === "visible" && !reregister && (
        <>
          <PayingCashLauncher
            sessionID={sessionID}
            onSuccess={() =>
              navigate({
                to: "/qr/$qrLookupID/gated/register/$sessionID/complete",
                search: { express },
              })
            }
          />
          <Separator orientation="horizontal" />
        </>
      )}
      <div>
        <Heading level={2}>
          {t("register.cart.sessionDetails.header", { ns: "gated" })}
        </Heading>
        <Text level={2} style={{ marginTop: "var(--space-2)" }}>
          {t("register.cart.sessionDetails.body", { ns: "gated" })}{" "}
          <a
            href={
              drawer?.externalLinks?.find((el) => el.type === "rates")?.link ||
              ""
            }
            target="_blank"
            rel="noreferrer"
          >
            {t("common.seeRates", { ns: "common" })}.
          </a>
        </Text>
      </div>
      {sessionRsp && <SessionDetails session={sessionRsp.session} />}
    </>
  )

  function onSubmit(data: DiscountCodeSchema) {
    mutate({ ...data, isRapidCheckout: express })
  }

  async function mutationFn(request: GatedPromoRegisterPOSTRequest) {
    const rsp = await api.gated.promoRegister.POST(request)
    if ("failReason" in rsp) {
      const shouldThrow = [
        PromoFailReason.InvalidRequestFormat,
        PromoFailReason.SystemError,
        PromoFailReason.Unauthorized,
      ]
      const error = new GatedPromoRegisterError(rsp.failReason)
      if (shouldThrow.some((r) => r === rsp.failReason)) {
        throw error
      }
      form.setError("promoCode", {
        message: t(error.translationKey, { ns: "errors" }),
      })
      return
    }

    if (rsp.bypassCC) {
      await navigate({
        to: "/qr/$qrLookupID/gated/register/$sessionID/confirmation",
        search: { key: rsp.confirmationKey!, express },
      })
      return
    }

    queryClient.removeQueries({
      ...gatedSetupIntentQueryOptions({ express: false }),
    })
    setDiscountCodeApplied({
      code: request.promoCode,
      redeemCopy: rsp.userFacingRedeemCopy,
    })
  }
}

function PaymentMethod() {
  const { t } = useTranslation(["gated"])
  return (
    <div>
      <Text level={3} className="center">
        {t("register.cart.payments.accepted")}
      </Text>
      <div className="payment-methods">
        <Icon name="paymentApplePay" retainOriginalColors />
        <Icon name="paymentGooglePay" retainOriginalColors />
        <Icon name="paymentVisa" retainOriginalColors />
        <Icon name="paymentMastercard" retainOriginalColors />
        <Icon name="paymentAmericanExpress" retainOriginalColors />
        <Icon name="paymentDiscover" retainOriginalColors />
        <Text level={3}>+ {t("register.cart.payments.more")}</Text>
      </div>
    </div>
  )
}

function SessionDetails(props: { session: GatedSessionLookupItem }) {
  const { t } = useTranslation(["gated"])
  return (
    <dl>
      <div>
        <dt>
          <b>{t("register.cart.sessionDetails.list.licensePlate")}</b>
        </dt>
        <dd>{normalizedAPILicensePlate(props.session.cameraPlate)}</dd>
      </div>
      <div>
        <dt>{t("register.cart.sessionDetails.list.lot")}</dt>
        <dd>{props.session.lotName}</dd>
      </div>
      <div>
        <dt>{t("register.cart.sessionDetails.list.entryTime")}</dt>
        <dd>
          {formatZonedDateTimeWithTimeZone(
            parseAbsoluteToLocal(props.session.startTime)
          )}
        </dd>
      </div>
    </dl>
  )
}
